import React, {
  createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import { decrypt } from '../utils/encryption';
import { useUserContext } from './UserContext';
import { FreezeStatus, getFreezeStatus } from '../services/confirmFreeze';

declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean;
  }
}

const FREEZE_STATUS_POLL_INTERVAL_MS = 60000;

interface GlobalInterface {
  jspreadsheetDataHasChanged: boolean,
  setJspreadsheetDataHasChanged: Dispatch<SetStateAction<boolean>>,
  serverErrorMessage: string,
  setServerErrorMessage: Dispatch<SetStateAction<string>>,
  freezeStatus: FreezeStatus | null,
  refreshFreezeStatus: () => void,
}

const GlobalContext = createContext<GlobalInterface>({} as GlobalInterface);
GlobalContext.displayName = 'GlobalContext';

interface Props {
  children: JSX.Element,
}
const api = axios.create({
  baseURL: config.api.url,
});
export default api;

export const GlobalProvider: React.FC<Props> = ({ children }) => {
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const interceptorsRegistered = useRef(false);
  if (!interceptorsRegistered.current) {
    interceptorsRegistered.current = true;

    api.interceptors.request.use(async (axiosConfig) => {
      const cookiesList = Cookies.get();
      if (config.auth.cookieName in cookiesList) {
        try {
          const decrypted = decrypt(cookiesList[config.auth.cookieName]);
          const parsed = JSON.parse(decrypted);
          if (parsed?.token && axiosConfig.headers) {
            axiosConfig.headers.authorization = `Bearer ${parsed.token}`;
          }
        } catch (error) {
          console.error('Token error', error);
          Cookies.remove(config.auth.cookieName);
        }
      }
      return axiosConfig;
    });

    api.interceptors.response.use((response) => {
      if (!response?.config?.silent) {
        setServerErrorMessage('');
      }
      return response;
    }, (error) => {
      if (error?.response?.status === 401) {
        Cookies.remove(config.auth.cookieName);
        return {};
      }
      if (error?.config?.silent) {
        return Promise.reject(error);
      }
      if (error?.response?.status >= 400) {
        const message = (error?.response?.status >= 400 && error?.response?.status <= 499)
          ? error?.response?.data?.message
          : 'Server error!';
        setServerErrorMessage(message);
        console.warn(message);
      } else {
        setServerErrorMessage('');
      }

      return Promise.reject(error);
    });
  }

  const [jspreadsheetDataHasChanged, setJspreadsheetDataHasChanged] = useState(false);

  const { isAuthenticated } = useUserContext();
  const [freezeStatus, setFreezeStatus] = useState<FreezeStatus | null>(null);
  const freezePollInFlight = useRef(false);

  const refreshFreezeStatus = useCallback(() => {
    freezePollInFlight.current = true;
    getFreezeStatus().then((response) => {
      if (response?.data) {
        setFreezeStatus((prev) => (JSON.stringify(prev) === JSON.stringify(response.data) ? prev : response.data));
      }
    }).catch(() => {
      // keep the last known status when the poll fails
    }).finally(() => {
      freezePollInFlight.current = false;
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setFreezeStatus(null);
      return undefined;
    }
    refreshFreezeStatus();
    const interval = setInterval(() => {
      if (!freezePollInFlight.current) {
        refreshFreezeStatus();
      }
    }, FREEZE_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      serverErrorMessage,
      jspreadsheetDataHasChanged,
      setJspreadsheetDataHasChanged,
      setServerErrorMessage,
      freezeStatus,
      refreshFreezeStatus,
    }),
    [jspreadsheetDataHasChanged, serverErrorMessage, freezeStatus],
  );

  return (
    <GlobalContext.Provider
      value={value}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = (): GlobalInterface => {
  const {
    jspreadsheetDataHasChanged, setJspreadsheetDataHasChanged, serverErrorMessage, setServerErrorMessage, freezeStatus, refreshFreezeStatus,
  } = useContext(GlobalContext);

  return {
    jspreadsheetDataHasChanged, setJspreadsheetDataHasChanged, serverErrorMessage, setServerErrorMessage, freezeStatus, refreshFreezeStatus,
  };
};
